// src/components/shop/ProductAdmin.js
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../../services/firebase';
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, orderBy } from 'firebase/firestore';

const ProductAdmin = () => {
  const [productData, setProductData] = useState({
    title: '',
    description: '',
    price: '',
    currency: 'USD',
    imageUrl: '',
    affiliateUrl: '',
    asin: '',
    category: '',
    tags: '',
    signals: '',
    brand: '',
    featured: false,
    status: 'active'
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [showJsonInput, setShowJsonInput] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  
  // shop management state
  const [allProducts, setAllProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [redundantGroups, setRedundantGroups] = useState([]);
  const [dismissedGroups, setDismissedGroups] = useState(new Set());
  const [dismissedItems, setDismissedItems] = useState(new Set());
  const [unavailableProducts, setUnavailableProducts] = useState([]);
  const [replacementSuggestions, setReplacementSuggestions] = useState({});
  const [categoryFilter, setCategoryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [showManagement, setShowManagement] = useState(false);
  const [imageSearching, setImageSearching] = useState(new Set());
  const [checkingAvailability, setCheckingAvailability] = useState(false);

  const categories = [
    'Furniture', 'Lighting', 'Decor', 'Art', 'Books', 'Textiles', 'Kitchen', 'Bath', 
    'Bedroom', 'Home & Garden', 'Storage & Organization', 'Cleaning & Maintenance', 
    'Safety & Security', 'Outdoor', 'Outdoor & Recreation', 'Sports', 'Fitness & Exercise', 'Travel & Luggage', 
    'Games & Entertainment', 'Hobbies & Crafts', 'Music & Audio', 'Photography & Video', 
    'Fashion & Apparel', 'Beauty & Personal Care', 'Health & Wellness', 'Jewelry & Accessories', 
    'Watches & Timepieces', 'Office & Business', 'Electronics & Tech', 'Automotive', 
    'Industrial & Scientific', 'Food & Grocery', 'Wine & Spirits', 'Coffee & Tea', 
    'Baby & Kids', 'Pet Supplies', 'Medical & Healthcare', 'Educational & Learning', 
    'Tools', 'Other'
  ];

  // Enhanced color/variant detection words
  const variantKeywords = [
    'black', 'white', 'red', 'blue', 'green', 'yellow', 'pink', 'purple', 'brown', 'gray', 'grey',
    'navy', 'beige', 'cream', 'ivory', 'silver', 'gold', 'bronze', 'copper',
    'small', 'medium', 'large', 'xl', 'xxl', 'xs',
    'mini', 'compact', 'standard', 'deluxe', 'premium', 'pro',
    'set of 2', 'set of 3', 'set of 4', 'pair', 'single',
    'ml', 'oz', 'liter', 'gallon', 'inch', 'ft', 'cm',
    '100ml', '200ml', '500ml', '1l', '2l'
  ];

  // Helper functions for similarity detection
  const getEditDistance = useCallback((str1, str2) => {
    const matrix = [];
    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j;
    }
    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[str2.length][str1.length];
  }, []);

  const calculateSimilarity = useCallback((str1, str2) => {
    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;
    const editDistance = getEditDistance(longer, shorter);
    return (longer.length - editDistance) / longer.length;
  }, [getEditDistance]);

  const detectVariant = useCallback((title1, title2) => {
    const t1Lower = title1.toLowerCase();
    const t2Lower = title2.toLowerCase();
    
    // Remove variant keywords and compare
    let t1Clean = t1Lower;
    let t2Clean = t2Lower;
    
    variantKeywords.forEach(keyword => {
      t1Clean = t1Clean.replace(new RegExp(`\\b${keyword}\\b`, 'g'), '').replace(/\s+/g, ' ').trim();
      t2Clean = t2Clean.replace(new RegExp(`\\b${keyword}\\b`, 'g'), '').replace(/\s+/g, ' ').trim();
    });
    
    // If cleaned titles are very similar, it's likely a variant
    const cleanSimilarity = calculateSimilarity(t1Clean, t2Clean);
    
    // Also check if one title contains variant keywords the other doesn't
    const t1HasVariant = variantKeywords.some(keyword => t1Lower.includes(keyword) && !t2Lower.includes(keyword));
    const t2HasVariant = variantKeywords.some(keyword => t2Lower.includes(keyword) && !t1Lower.includes(keyword));
    
    return cleanSimilarity > 0.8 && (t1HasVariant || t2HasVariant);
  }, [calculateSimilarity, variantKeywords]);

  const calculateTitleSimilarity = useCallback((title1, title2) => {
    // First check for variants
    if (detectVariant(title1, title2)) {
      return 0.7; // Lower similarity for variants
    }
    
    return calculateSimilarity(title1.toLowerCase(), title2.toLowerCase());
  }, [detectVariant, calculateSimilarity]);

  const detectRedundancies = useCallback((products) => {
    const groups = [];
    const processed = new Set();
    
    products.forEach((product, index) => {
      if (processed.has(product.id)) return;
      
      const duplicates = products.filter((other, otherIndex) => {
        if (otherIndex <= index || processed.has(other.id)) return false;
        
        // Enhanced redundancy detection
        const sameASIN = product.asin && other.asin && product.asin === other.asin;
        
        // More sophisticated title similarity that accounts for variants
        const titleSimilarity = calculateTitleSimilarity(product.title, other.title);
        const isVariant = detectVariant(product.title, other.title);
        
        // Only consider similar titles as redundant if they're not clearly variants
        const similarTitleNotVariant = titleSimilarity > 0.85 && !isVariant;
        
        const sameImage = product.image_url && other.image_url && 
          product.image_url === other.image_url;
        
        // Enhanced brand + category check
        const sameBrandAndType = product.vendor_name && other.vendor_name &&
          product.vendor_name.toLowerCase() === other.vendor_name.toLowerCase() &&
          product.category === other.category &&
          titleSimilarity > 0.9 && !isVariant;
        
        return sameASIN || similarTitleNotVariant || sameImage || sameBrandAndType;
      });
      
      if (duplicates.length > 0) {
        const group = [product, ...duplicates];
        const groupId = `group_${index}`;
        
        // Don't show if group is dismissed
        if (!dismissedGroups.has(groupId)) {
          groups.push({
            id: groupId,
            type: product.asin && duplicates.some(d => d.asin === product.asin) ? 'Same ASIN' :
                  product.image_url && duplicates.some(d => d.image_url === product.image_url) ? 'Same Image' :
                  'Similar Product',
            products: group.sort((a, b) => new Date(b.createdAt?.seconds * 1000) - new Date(a.createdAt?.seconds * 1000))
          });
        }
        
        group.forEach(p => processed.add(p.id));
      }
    });
    
    setRedundantGroups(groups);
  }, [dismissedGroups, calculateTitleSimilarity, detectVariant]);

  const loadAllProducts = useCallback(async () => {
    setLoadingProducts(true);
    try {
      const q = query(collection(db, 'products'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const products = [];
      
      querySnapshot.forEach((doc) => {
        products.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setAllProducts(products);
      detectRedundancies(products);
      setMessage(`✅ Loaded ${products.length} products`);
    } catch (error) {
      console.error('Error loading products:', error);
      setMessage(`❌ Error loading products: ${error.message}`);
    } finally {
      setLoadingProducts(false);
    }
  }, [detectRedundancies]);

  const filterAndSortProducts = useCallback(() => {
    let filtered = [...allProducts];
    
    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(product => product.category === categoryFilter);
    }
    
    // Apply status filter
    if (statusFilter) {
      if (statusFilter === 'redundant') {
        const redundantIds = new Set();
        redundantGroups.forEach(group => {
          group.products.forEach(product => redundantIds.add(product.id));
        });
        filtered = filtered.filter(product => redundantIds.has(product.id));
      } else if (statusFilter === 'unavailable') {
        filtered = unavailableProducts;
      } else {
        filtered = filtered.filter(product => product.status === statusFilter);
      }
    }
    
    // Apply sorting
    switch (sortBy) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.createdAt?.seconds * 1000) - new Date(a.createdAt?.seconds * 1000));
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.createdAt?.seconds * 1000) - new Date(b.createdAt?.seconds * 1000));
        break;
      case 'price-high':
        filtered.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'price-low':
        filtered.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'title':
        filtered.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
        break;
      case 'brand':
        filtered.sort((a, b) => (a.vendor_name || '').localeCompare(b.vendor_name || ''));
        break;
      default:
        break;
    }
    
    setFilteredProducts(filtered);
  }, [allProducts, categoryFilter, statusFilter, sortBy, redundantGroups, unavailableProducts]);

  // Load all products on component mount
  useEffect(() => {
    if (showManagement) {
      loadAllProducts();
    }
  }, [showManagement, loadAllProducts]);

  // Filter and sort products when filters change
  useEffect(() => {
    filterAndSortProducts();
  }, [filterAndSortProducts]);

  // Product availability checking
  const checkProductAvailability = async () => {
    setCheckingAvailability(true);
    const unavailable = [];
    const suggestions = {};
    
    try {
      setMessage('🔍 Checking product availability...');
      
      for (const product of allProducts) {
        if (product.asin && product.vendor_url) {
          try {
            // Check if product is still available on Amazon
            const isAvailable = await checkAmazonProductAvailability(product.asin);
            
            if (!isAvailable) {
              unavailable.push(product);
              
              // Search for replacement suggestions
              const replacements = await findReplacementProducts(product);
              if (replacements.length > 0) {
                suggestions[product.id] = replacements;
              }
            }
          } catch (error) {
            console.error(`Error checking availability for ${product.id}:`, error);
          }
        }
      }
      
      setUnavailableProducts(unavailable);
      setReplacementSuggestions(suggestions);
      
      if (unavailable.length > 0) {
        setMessage(`⚠️ Found ${unavailable.length} unavailable products with ${Object.keys(suggestions).length} replacement suggestions`);
      } else {
        setMessage('✅ All products are currently available');
      }
      
    } catch (error) {
      console.error('Error checking product availability:', error);
      setMessage('❌ Error checking product availability');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const checkAmazonProductAvailability = async (asin) => {
    try {
      // This would typically use Amazon Product Advertising API
      // For now, we'll simulate with a simple fetch check
      const testUrl = `https://www.amazon.com/dp/${asin}`;
      // eslint-disable-next-line no-unused-vars
      const response = await fetch(testUrl, { method: 'HEAD', mode: 'no-cors' });
      
      // Since no-cors doesn't give us the status, we'll use a different approach
      // In production, you'd use the Amazon Product Advertising API
      return true; // Placeholder - implement proper availability check
    } catch (error) {
      console.error('Error checking product availability:', error);
      return false;
    }
  };

  const findReplacementProducts = async (product) => {
    try {
      // This would search for similar products from other vendors
      // For now, return mock replacement suggestions
      return [
        {
          title: `Similar to ${product.title}`,
          vendor: 'Alternative Vendor',
          price: product.price * 1.1,
          url: 'https://example.com/replacement',
          confidence: 0.85
        }
      ];
    } catch (error) {
      console.error('Error finding replacement products:', error);
      return [];
    }
  };

  const handleRemoveUnavailableProduct = async (productId) => {
    if (!window.confirm('Remove this unavailable product from the store?')) return;
    
    setIsLoading(true);
    try {
      await updateDoc(doc(db, 'shop', productId), {
        status: 'unavailable',
        unavailableDate: new Date(),
        updatedAt: new Date()
      });
      
      setMessage('✅ Product marked as unavailable');
      await loadAllProducts();
    } catch (error) {
      console.error('Error removing unavailable product:', error);
      setMessage('❌ Error updating product status');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveReplacement = async (originalProductId, replacement) => {
    if (!window.confirm(`Add replacement product "${replacement.title}"?`)) return;
    
    setIsLoading(true);
    try {
      // Add new replacement product
      const newProduct = {
        title: replacement.title,
        description: `Replacement for discontinued product`,
        price: replacement.price,
        currency: 'USD',
        vendor_url: replacement.url,
        vendor_name: replacement.vendor,
        category: allProducts.find(p => p.id === originalProductId)?.category || 'Other',
        tags: allProducts.find(p => p.id === originalProductId)?.tags || [],
        signals: allProducts.find(p => p.id === originalProductId)?.signals || [],
        featured: false,
        status: 'active',
        source: 'replacement',
        replacedProduct: originalProductId,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await addDoc(collection(db, 'shop'), newProduct);
      
      // Mark original as replaced
      await updateDoc(doc(db, 'shop', originalProductId), {
        status: 'replaced',
        replacedDate: new Date(),
        updatedAt: new Date()
      });
      
      setMessage('✅ Replacement product added successfully');
      await loadAllProducts();
    } catch (error) {
      console.error('Error adding replacement product:', error);
      setMessage('❌ Error adding replacement product');
    } finally {
      setIsLoading(false);
    }
  };

  // Enhanced image fitting options
  const getImageFitStyle = (imageUrl, fitMode = 'cover') => {
    const baseStyle = {
      width: '100%',
      height: '81.6px',
      borderRadius: '4.08px'
    };

    switch (fitMode) {
      case 'contain':
        return {
          ...baseStyle,
          objectFit: 'contain',
          backgroundColor: '#f8f9fa'
        };
      case 'cover':
        return {
          ...baseStyle,
          objectFit: 'cover'
        };
      case 'fill':
        return {
          ...baseStyle,
          objectFit: 'fill'
        };
      default:
        return {
          ...baseStyle,
          objectFit: 'cover'
        };
    }
  };

  const handleProductSelect = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedProducts(newSelected);
  };

  const handleSelectAll = () => {
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProducts.size === 0) {
      setMessage('❌ No products selected');
      return;
    }
    
    const confirmMessage = `Are you sure you want to delete ${selectedProducts.size} selected products? This cannot be undone.`;
    if (!window.confirm(confirmMessage)) return;
    
    setIsLoading(true);
    try {
      const deletePromises = Array.from(selectedProducts).map(productId => 
        deleteDoc(doc(db, 'shop', productId))
      );
      
      await Promise.all(deletePromises);
      setMessage(`✅ Successfully deleted ${selectedProducts.size} products`);
      setSelectedProducts(new Set());
      await loadAllProducts();
    } catch (error) {
      console.error('Error deleting products:', error);
      setMessage(`❌ Error deleting products: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteSingle = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    
    setIsLoading(true);
    try {
      await deleteDoc(doc(db, 'shop', productId));
      setMessage('✅ Product deleted successfully');
      await loadAllProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      setMessage(`❌ Error deleting product: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteRedundantGroup = async (group, keepNewest = true) => {
    const productsToDelete = keepNewest ? group.products.slice(1) : group.products;
    const confirmMessage = `Delete ${productsToDelete.length} ${keepNewest ? 'older' : ''} products from this ${group.type.toLowerCase()} group?`;
    
    if (!window.confirm(confirmMessage)) return;
    
    setIsLoading(true);
    try {
      const deletePromises = productsToDelete.map(product => 
        deleteDoc(doc(db, 'shop', product.id))
      );
      
      await Promise.all(deletePromises);
      setMessage(`✅ Deleted ${productsToDelete.length} redundant products`);
      await loadAllProducts();
    } catch (error) {
      console.error('Error deleting redundant products:', error);
      setMessage(`❌ Error deleting products: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // New dismiss functions
  const handleDismissGroup = (groupId) => {
    const newDismissed = new Set(dismissedGroups);
    newDismissed.add(groupId);
    setDismissedGroups(newDismissed);
    
    // Remove from current redundant groups
    setRedundantGroups(prev => prev.filter(group => group.id !== groupId));
    setMessage('✅ Redundancy group dismissed');
  };

  const handleDismissItem = (groupId, itemId) => {
    const newDismissed = new Set(dismissedItems);
    newDismissed.add(`${groupId}_${itemId}`);
    setDismissedItems(newDismissed);
    
    // Update the group to remove the dismissed item
    setRedundantGroups(prev => prev.map(group => {
      if (group.id === groupId) {
        const updatedProducts = group.products.filter(p => p.id !== itemId);
        return updatedProducts.length > 1 ? { ...group, products: updatedProducts } : null;
      }
      return group;
    }).filter(Boolean));
    
    setMessage('✅ Item dismissed from redundancy group');
  };

  // Enhanced image search function
  const searchBetterImage = async (productId, asin, title) => {
    if (!asin) {
      setMessage('❌ No ASIN available for image search');
      return;
    }
    
    setImageSearching(prev => new Set([...prev, productId]));
    
    try {
      // Multiple Amazon image URL patterns to try
      const imageUrlPatterns = [
        `https://images-na.ssl-images-amazon.com/images/P/${asin}.01._SL1500_.jpg`,
        `https://images-na.ssl-images-amazon.com/images/P/${asin}.01._AC_SL1500_.jpg`,
        `https://m.media-amazon.com/images/I/${asin}._AC_SL1500_.jpg`,
        `https://images-na.ssl-images-amazon.com/images/P/${asin}.01.L.jpg`,
        `https://m.media-amazon.com/images/I/${asin}.jpg`
      ];
      
      // Try each URL pattern until we find a working one
      for (const imageUrl of imageUrlPatterns) {
        try {
          const img = new Image();
          const imageLoaded = await new Promise((resolve) => {
            img.onload = () => resolve(true);
            img.onerror = () => resolve(false);
            img.src = imageUrl;
          });
          
          if (imageLoaded) {
            // Update the product with better image
            await updateDoc(doc(db, 'shop', productId), {
              image_url: imageUrl,
              updatedAt: new Date()
            });
            
            setMessage('✅ Image updated with higher resolution version');
            await loadAllProducts();
            break;
          }
        } catch (error) {
          console.error(`Error testing image URL ${imageUrl}:`, error);
          continue;
        }
      }
      
    } catch (error) {
      console.error('Error searching for better image:', error);
      setMessage('❌ Error searching for better image');
    } finally {
      setImageSearching(prev => {
        const newSet = new Set(prev);
        newSet.delete(productId);
        return newSet;
      });
    }
  };

  // Form handling functions
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setProductData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const extractASINFromURL = (url) => {
    const asinMatch = url.match(/\/dp\/([A-Z0-9]{10})|\/product\/([A-Z0-9]{10})|\/([A-Z0-9]{10})/);
    return asinMatch ? (asinMatch[1] || asinMatch[2] || asinMatch[3]) : '';
  };

  const handleAffiliateUrlChange = (e) => {
    const url = e.target.value;
    setProductData(prev => ({
      ...prev,
      affiliateUrl: url,
      asin: extractASINFromURL(url)
    }));
  };

  const handlePasteFromClipboard = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setJsonInput(clipboardText);
      setMessage('✅ Data pasted from clipboard! Click Auto-Fill Form to populate fields.');
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      setMessage('❌ Could not read clipboard. Try pasting manually with Cmd+V.');
    }
  };

  const handleAutoFill = () => {
    if (!jsonInput || !jsonInput.trim()) {
      setMessage('❌ Please paste JSON data first.');
      return;
    }
  
    try {
      const parsedData = JSON.parse(jsonInput.trim());
      
      setProductData(prev => ({
        ...prev,
        title: parsedData.title || '',
        description: parsedData.description || '',
        price: parsedData.price?.toString().replace(/[^0-9.]/g, '') || '',
        imageUrl: parsedData.imageUrl || '',
        affiliateUrl: parsedData.affiliateUrl || '',
        asin: parsedData.asin || '',
        brand: parsedData.brand || '',
        category: parsedData.category || '', // This should now populate from Claude
        tags: Array.isArray(parsedData.tags) ? parsedData.tags.join(', ') : (parsedData.tags || ''),
        signals: Array.isArray(parsedData.signals) ? parsedData.signals.join(', ') : (parsedData.signals || '')
      }));
      
      setMessage('✅ All fields filled including category! Review and submit.');
      setJsonInput('');
      
    } catch (error) {
      console.error('JSON Parse Error:', error);
      setMessage('❌ Invalid JSON format.');
    }
  };

  const clearForm = () => {
    setProductData({
      title: '',
      description: '',
      price: '',
      currency: 'USD',
      imageUrl: '',
      affiliateUrl: '',
      asin: '',
      category: '',
      tags: '',
      signals: '',
      brand: '',
      featured: false,
      status: 'active'
    });
    setJsonInput('');
    setMessage('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage('');

    try {
      const tags = productData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      const signals = productData.signals.split(',').map(signal => signal.trim()).filter(signal => signal);

      const newProduct = {
        title: productData.title,
        description: productData.description,
        price: parseFloat(productData.price),
        currency: productData.currency,
        image_url: productData.imageUrl,
        vendor_url: productData.affiliateUrl,
        asin: productData.asin,
        category: productData.category,
        tags: tags,
        signals: signals,
        vendor_name: productData.brand,
        featured: productData.featured,
        status: productData.status,
        source: 'manual',
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const docRef = await addDoc(collection(db, 'products'), newProduct);
      
      setMessage(`✅ Product added successfully! ID: ${docRef.id}`);
      clearForm();
      
      // Refresh products if management view is open
      if (showManagement) {
        await loadAllProducts();
      }

    } catch (error) {
      console.error('Error adding product:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={{
      maxWidth: '816px',
      margin: '1.36rem auto',
      padding: '1.36rem',
      backgroundColor: 'white',
      borderRadius: '8.16px',
      boxShadow: '0 2.72px 8.16px rgba(0, 0, 0, 0.08)'
    }}>
      {/* Header with Navigation */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.36rem' }}>
        <h2 style={{ margin: 0, color: '#333', fontSize: '1.36rem' }}>Product Admin</h2>
        <div style={{ display: 'flex', gap: '0.68rem' }}>
          <button
            onClick={checkProductAvailability}
            disabled={checkingAvailability}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: checkingAvailability ? '#ccc' : '#ffc107',
              color: checkingAvailability ? '#666' : '#000',
              border: 'none',
              borderRadius: '4.08px',
              cursor: checkingAvailability ? 'not-allowed' : 'pointer',
              fontSize: '0.612rem'
            }}
          >
            {checkingAvailability ? '⟳ Checking...' : '🔍 Check Availability'}
          </button>
          
          <button
            onClick={() => setShowManagement(!showManagement)}
            style={{
              padding: '0.51rem 1.02rem',
              backgroundColor: showManagement ? '#dc3545' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4.08px',
              cursor: 'pointer',
              fontSize: '0.612rem'
            }}
          >
            {showManagement ? '📝 Add Products' : '🛍️ Manage shop'}
          </button>
        </div>
      </div>

      {!showManagement ? (
        // COMPLETE ADD PRODUCT FORM
        <div>
          {/* Enhanced Paste Section */}
          <div style={{ marginBottom: '1.36rem', padding: '0.68rem', backgroundColor: '#f8f9fa', borderRadius: '5.44px' }}>
            <div style={{ display: 'flex', gap: '0.68rem', marginBottom: '0.68rem' }}>
              <button
                type="button"
                onClick={() => setShowJsonInput(!showJsonInput)}
                style={{
                  padding: '0.34rem 0.68rem',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4.08px',
                  cursor: 'pointer',
                  fontSize: '0.612rem'
                }}
              >
                📋 Paste Bookmarklet Data
              </button>
              
              <button
                type="button"
                onClick={handlePasteFromClipboard}
                style={{
                  padding: '0.34rem 0.68rem',
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4.08px',
                  cursor: 'pointer',
                  fontSize: '0.612rem'
                }}
              >
                📥 Paste from Clipboard
              </button>
              
              <button
                type="button"
                onClick={clearForm}
                style={{
                  padding: '0.34rem 0.68rem',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4.08px',
                  cursor: 'pointer',
                  fontSize: '0.612rem'
                }}
              >
                🗑️ Clear All
              </button>
            </div>
            
            {showJsonInput && (
              <div>
                <textarea
                  value={jsonInput}
                  onChange={(e) => setJsonInput(e.target.value)}
                  placeholder='Paste your bookmarklet JSON here, or use "Paste from Clipboard" button above'
                  rows="4"
                  style={{
                    width: '100%',
                    padding: '0.51rem',
                    border: '0.68px solid #ddd',
                    borderRadius: '4.08px',
                    fontSize: '0.544rem',
                    fontFamily: 'monospace',
                    marginBottom: '0.34rem'
                  }}
                />
                <button
                  type="button"
                  onClick={handleAutoFill}
                  style={{
                    padding: '0.34rem 0.68rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4.08px',
                    cursor: 'pointer',
                    fontSize: '0.612rem'
                  }}
                >
                  ✨ Auto-Fill Form
                </button>
              </div>
            )}
          </div>
          
          {/* COMPLETE FORM WITH ALL FIELDS */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '0.68rem' }}>
              <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                Product Title *
              </label>
              <input
                type="text"
                name="title"
                value={productData.title}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '0.51rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '4.08px',
                  fontSize: '0.68rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '0.68rem' }}>
              <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                Description
              </label>
              <textarea
                name="description"
                value={productData.description}
                onChange={handleInputChange}
                rows="3"
                style={{
                  width: '100%',
                  padding: '0.51rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '4.08px',
                  fontSize: '0.68rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.68rem', marginBottom: '0.68rem' }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                  Price *
                </label>
                <input
                  type="number"
                  name="price"
                  value={productData.price}
                  onChange={handleInputChange}
                  step="0.01"
                  required
                  style={{
                    width: '100%',
                    padding: '0.51rem',
                    border: '0.68px solid #ddd',
                    borderRadius: '4.08px',
                    fontSize: '0.68rem'
                  }}
                />
              </div>
              
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                  Currency
                </label>
                <select
                  name="currency"
                  value={productData.currency}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.51rem',
                    border: '0.68px solid #ddd',
                    borderRadius: '4.08px',
                    fontSize: '0.68rem'
                  }}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '0.68rem' }}>
              <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                Brand
              </label>
              <input
                type="text"
                name="brand"
                value={productData.brand}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.51rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '4.08px',
                  fontSize: '0.68rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '0.68rem' }}>
              <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                Amazon Affiliate Link *
              </label>
              <input
                type="url"
                name="affiliateUrl"
                value={productData.affiliateUrl}
                onChange={handleAffiliateUrlChange}
                required
                placeholder="https://amzn.to/..."
                style={{
                  width: '100%',
                  padding: '0.51rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '4.08px',
                  fontSize: '0.68rem'
                }}
              />
              {productData.asin && (
                <small style={{ color: '#666', fontSize: '0.544rem' }}>
                  ASIN detected: {productData.asin}
                </small>
              )}
            </div>

            <div style={{ marginBottom: '0.68rem' }}>
              <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                Product Image URL *
              </label>
              <input
                type="url"
                name="imageUrl"
                value={productData.imageUrl}
                onChange={handleInputChange}
                required
                style={{
                  width: '100%',
                  padding: '0.51rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '4.08px',
                  fontSize: '0.68rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '0.68rem' }}>
              <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                Category
              </label>
              <select
                name="category"
                value={productData.category}
                onChange={handleInputChange}
                style={{
                  width: '100%',
                  padding: '0.51rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '4.08px',
                  fontSize: '0.68rem'
                }}
              >
                <option value="">Select a category</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '0.68rem' }}>
              <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                Tags (comma-separated)
              </label>
              <input
                type="text"
                name="tags"
                value={productData.tags}
                onChange={handleInputChange}
                placeholder="e.g. modern, furniture, design"
                style={{
                  width: '100%',
                  padding: '0.51rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '4.08px',
                  fontSize: '0.68rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '0.68rem' }}>
              <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                Signals (for Digital Card matching, comma-separated)
              </label>
              <input
                type="text"
                name="signals"
                value={productData.signals}
                onChange={handleInputChange}
                placeholder="e.g. aesthetic, functional, modern"
                style={{
                  width: '100%',
                  padding: '0.51rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '4.08px',
                  fontSize: '0.68rem'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.68rem', marginBottom: '1.02rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.34rem' }}>
                <input
                  type="checkbox"
                  name="featured"
                  checked={productData.featured}
                  onChange={handleInputChange}
                />
                <span style={{ fontWeight: '600', fontSize: '0.68rem' }}>Featured Product</span>
              </label>

              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', marginBottom: '0.34rem', fontWeight: '600', fontSize: '0.68rem' }}>
                  Status
                </label>
                <select
                  name="status"
                  value={productData.status}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '0.51rem',
                    border: '0.68px solid #ddd',
                    borderRadius: '4.08px',
                    fontSize: '0.68rem'
                  }}
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              style={{
                width: '100%',
                padding: '0.51rem',
                backgroundColor: isLoading ? '#ccc' : '#0d47a1',
                color: 'white',
                border: 'none',
                borderRadius: '4.08px',
                fontSize: '0.68rem',
                fontWeight: '600',
                cursor: isLoading ? 'not-allowed' : 'pointer'
              }}
            >
              {isLoading ? 'Adding Product...' : 'Add Product'}
            </button>
          </form>

          {/* ENHANCED PREVIEW */}
          {productData.imageUrl && (
            <div style={{ marginTop: '1.36rem' }}>
              <h3 style={{ marginBottom: '0.68rem', fontSize: '1.02rem' }}>Preview</h3>
              <div style={{
                border: '0.68px solid #ddd',
                borderRadius: '5.44px',
                padding: '0.68rem',
                backgroundColor: '#f9f9f9'
              }}>
                <div style={{ display: 'flex', gap: '0.68rem', marginBottom: '0.68rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.544rem', fontWeight: '600', marginBottom: '0.34rem' }}>Cover Fit</div>
                    <img 
                      src={productData.imageUrl} 
                      alt="Cover fit preview" 
                      style={getImageFitStyle(productData.imageUrl, 'cover')}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '0.544rem', fontWeight: '600', marginBottom: '0.34rem' }}>Contain Fit</div>
                    <img 
                      src={productData.imageUrl} 
                      alt="Contain fit preview" 
                      style={getImageFitStyle(productData.imageUrl, 'contain')}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
                
                <div style={{ padding: '0.34rem', backgroundColor: 'white', borderRadius: '2.72px' }}>
                  <p style={{ margin: '0.34rem 0', fontWeight: '600', fontSize: '0.68rem' }}>{productData.title}</p>
                  <p style={{ margin: '0.34rem 0', color: '#666', fontSize: '0.68rem' }}>${productData.price} {productData.currency}</p>
                  <p style={{ margin: '0.34rem 0', fontSize: '0.544rem', color: '#888' }}>
                    {productData.category} - {productData.brand}
                  </p>
                  {productData.description && (
                    <p style={{ margin: '0.34rem 0', fontSize: '0.544rem', color: '#666' }}>
                      {productData.description.length > 100 ? 
                        productData.description.substring(0, 100) + '...' : 
                        productData.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        // SHOP MANAGEMENT INTERFACE
        <div>
          {/* Filters and Controls */}
          <div style={{ 
            display: 'flex', 
            gap: '0.68rem', 
            marginBottom: '1.02rem', 
            padding: '0.68rem', 
            backgroundColor: '#f8f9fa', 
            borderRadius: '5.44px',
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, minWidth: '136px' }}>
              <label style={{ display: 'block', marginBottom: '0.17rem', fontSize: '0.612rem', fontWeight: '600' }}>
                Category Filter
              </label>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.34rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '2.72px',
                  fontSize: '0.612rem'
                }}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            
            <div style={{ flex: 1, minWidth: '102px' }}>
              <label style={{ display: 'block', marginBottom: '0.17rem', fontSize: '0.612rem', fontWeight: '600' }}>
                Status Filter
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.34rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '2.72px',
                  fontSize: '0.612rem'
                }}
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="redundant">Redundant</option>
                <option value="unavailable">Unavailable</option>
              </select>
            </div>
            
            <div style={{ flex: 1, minWidth: '102px' }}>
              <label style={{ display: 'block', marginBottom: '0.17rem', fontSize: '0.612rem', fontWeight: '600' }}>
                Sort By
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.34rem',
                  border: '0.68px solid #ddd',
                  borderRadius: '2.72px',
                  fontSize: '0.612rem'
                }}
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
                <option value="price-high">Price High to Low</option>
                <option value="price-low">Price Low to High</option>
                <option value="title">Title A-Z</option>
                <option value="brand">Brand A-Z</option>
              </select>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'end', gap: '0.34rem' }}>
              <button
                onClick={loadAllProducts}
                disabled={loadingProducts}
                style={{
                  padding: '0.34rem 0.68rem',
                  backgroundColor: '#17a2b8',
                  color: 'white',
                  border: 'none',
                  borderRadius: '2.72px',
                  cursor: 'pointer',
                  fontSize: '0.612rem'
                }}
              >
                {loadingProducts ? '⟳' : '🔄'} Refresh
              </button>
            </div>
          </div>

          {/* Unavailable Products Section */}
          {unavailableProducts.length > 0 && (
            <div style={{ marginBottom: '1.36rem' }}>
              <h3 style={{ marginBottom: '0.68rem', color: '#dc3545', fontSize: '1.02rem' }}>
                ❌ Unavailable Products ({unavailableProducts.length} found)
              </h3>
              
              {unavailableProducts.map((product) => (
                <div key={product.id} style={{
                  border: '1.36px solid #dc3545',
                  borderRadius: '5.44px',
                  padding: '0.68rem',
                  marginBottom: '0.68rem',
                  backgroundColor: '#fff5f5'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.25rem 0', color: '#dc3545' }}>
                        {product.title}
                      </h4>
                      <p style={{ margin: 0, fontSize: '0.85rem', color: '#666' }}>
                        ${product.price} • {product.vendor_name}
                      </p>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {replacementSuggestions[product.id] && (
                        <button
                          onClick={() => handleApproveReplacement(product.id, replacementSuggestions[product.id][0])}
                          style={{
                            padding: '0.34rem 0.68rem',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '2.72px',
                            cursor: 'pointer',
                            fontSize: '0.544rem'
                          }}
                        >
                          ✅ Add Replacement
                        </button>
                      )}
                      
                      <button
                        onClick={() => handleRemoveUnavailableProduct(product.id)}
                        style={{
                          padding: '0.34rem 0.68rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '2.72px',
                          cursor: 'pointer',
                          fontSize: '0.544rem'
                        }}
                      >
                        🗑️ Remove
                      </button>
                    </div>
                  </div>
                  
                  {replacementSuggestions[product.id] && (
                    <div style={{ marginTop: '0.68rem', padding: '0.34rem', backgroundColor: 'white', borderRadius: '2.72px' }}>
                      <strong>Suggested Replacement:</strong>
                      <div style={{ fontSize: '0.578rem', color: '#666', marginTop: '0.17rem' }}>
                        {replacementSuggestions[product.id][0].title} - ${replacementSuggestions[product.id][0].price}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Bulk Actions */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '0.68rem',
            padding: '0.68rem',
            backgroundColor: '#e9ecef',
            borderRadius: '4.08px'
          }}>
            <div>
              <button
                onClick={handleSelectAll}
                style={{
                  padding: '0.34rem 0.68rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '2.72px',
                  cursor: 'pointer',
                  fontSize: '0.612rem',
                  marginRight: '0.68rem'
                }}
              >
                {selectedProducts.size === filteredProducts.length ? 'Deselect All' : 'Select All'}
              </button>
              
              <span style={{ fontSize: '0.612rem', color: '#666' }}>
                {selectedProducts.size} of {filteredProducts.length} selected
              </span>
            </div>
            
            <button
              onClick={handleBulkDelete}
              disabled={selectedProducts.size === 0 || isLoading}
              style={{
                padding: '0.34rem 0.68rem',
                backgroundColor: selectedProducts.size > 0 ? '#dc3545' : '#ccc',
                color: 'white',
                border: 'none',
                borderRadius: '2.72px',
                cursor: selectedProducts.size > 0 ? 'pointer' : 'not-allowed',
                fontSize: '0.612rem'
              }}
            >
              🗑️ Delete Selected ({selectedProducts.size})
            </button>
          </div>

          {/* Enhanced Redundancy Groups */}
          {redundantGroups.length > 0 && (
            <div style={{ marginBottom: '1.36rem' }}>
              <h3 style={{ marginBottom: '0.68rem', color: '#dc3545', fontSize: '1.02rem' }}>
                ⚠️ Potential Redundant Products ({redundantGroups.length} groups found)
              </h3>
              
              {redundantGroups.map((group) => (
                <div key={group.id} style={{
                  border: '1.36px solid #dc3545',
                  borderRadius: '5.44px',
                  padding: '0.68rem',
                  marginBottom: '0.68rem',
                  backgroundColor: '#fff5f5'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.68rem' }}>
                    <div>
                      <h4 style={{ margin: '0 0 0.17rem 0', color: '#dc3545', fontSize: '0.816rem' }}>
                        {group.type} - {group.products.length} products
                      </h4>
                      <p style={{ margin: 0, fontSize: '0.578rem', color: '#666' }}>
                        Newest: {new Date(group.products[0].createdAt?.seconds * 1000).toLocaleDateString()}
                      </p>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '0.34rem' }}>
                      <button
                        onClick={() => handleDeleteRedundantGroup(group, true)}
                        style={{
                          padding: '0.34rem 0.68rem',
                          backgroundColor: '#ffc107',
                          color: '#000',
                          border: 'none',
                          borderRadius: '2.72px',
                          cursor: 'pointer',
                          fontSize: '0.544rem'
                        }}
                      >
                        Keep Newest, Delete {group.products.length - 1}
                      </button>
                      
                      <button
                        onClick={() => handleDeleteRedundantGroup(group, false)}
                        style={{
                          padding: '0.34rem 0.68rem',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '2.72px',
                          cursor: 'pointer',
                          fontSize: '0.544rem'
                        }}
                      >
                        Delete All {group.products.length}
                      </button>
                      
                      <button
                        onClick={() => handleDismissGroup(group.id)}
                        style={{
                          padding: '0.34rem 0.68rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '2.72px',
                          cursor: 'pointer',
                          fontSize: '0.544rem'
                        }}
                      >
                        ✕ Dismiss Group
                      </button>
                    </div>
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(92.48px, 1fr))', gap: '0.34rem' }}>
                    {group.products.map((product) => {
                      const isDismissed = dismissedItems.has(`${group.id}_${product.id}`);
                      
                      if (isDismissed) return null;
                      
                      return (
                        <div key={product.id} style={{
                          border: '0.68px solid #ddd',
                          borderRadius: '2.72px',
                          padding: '0.34rem',
                          backgroundColor: 'white',
                          fontSize: '0.544rem',
                          position: 'relative'
                        }}>
                          <button
                            onClick={() => handleDismissItem(group.id, product.id)}
                            style={{
                              position: 'absolute',
                              top: '0.17rem',
                              right: '0.17rem',
                              background: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '50%',
                              width: '13.6px',
                              height: '13.6px',
                              fontSize: '0.476rem',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ✕
                          </button>
                          
                          <div style={{ display: 'flex', gap: '0.34rem', paddingRight: '1.02rem' }}>
                            <img 
                              src={product.image_url} 
                              alt={product.title}
                              style={{
                                width: '27.2px',
                                height: '27.2px',
                                objectFit: 'contain',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '2.72px',
                                flexShrink: 0
                              }}
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: '600', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {product.title}
                              </div>
                              <div style={{ color: '#666', fontSize: '0.51rem' }}>
                                ${product.price} • {product.vendor_name}
                              </div>
                              <div style={{ color: '#999', fontSize: '0.476rem' }}>
                                {new Date(product.createdAt?.seconds * 1000).toLocaleDateString()}
                              </div>
                              {product.asin && (
                                <div style={{ color: '#999', fontSize: '0.442rem', marginTop: '0.17rem' }}>
                                  ASIN: {product.asin}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Products Grid */}
          {loadingProducts ? (
            <div style={{ textAlign: 'center', padding: '1.36rem', fontSize: '0.748rem', color: '#666' }}>
              Loading products...
            </div>
          ) : (
            <div>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(129.472px, 1fr))', 
                gap: '0.68rem' 
              }}>
                {filteredProducts.map((product) => {
                  const isRedundant = redundantGroups.some(group => 
                    group.products.some(p => p.id === product.id)
                  );
                  
                  return (
                    <div key={product.id} style={{
                      border: isRedundant ? '1.36px solid #ffc107' : '0.68px solid #ddd',
                      borderRadius: '5.44px',
                      padding: '0.68rem',
                      backgroundColor: isRedundant ? '#fffbf0' : 'white',
                      position: 'relative'
                    }}>
                      {/* Selection checkbox */}
                      <input
                        type="checkbox"
                        checked={selectedProducts.has(product.id)}
                        onChange={() => handleProductSelect(product.id)}
                        style={{
                          position: 'absolute',
                          top: '0.34rem',
                          left: '0.34rem',
                          transform: 'scale(0.816)'
                        }}
                      />
                      
                      {/* Redundant indicator */}
                      {isRedundant && (
                        <div style={{
                          position: 'absolute',
                          top: '0.34rem',
                          right: '0.34rem',
                          backgroundColor: '#ffc107',
                          color: '#000',
                          padding: '0.17rem 0.34rem',
                          borderRadius: '2.72px',
                          fontSize: '0.476rem',
                          fontWeight: '600'
                        }}>
                          POTENTIAL
                        </div>
                      )}
                      
                      {/* Product image with enhance button */}
                      <div style={{ marginBottom: '0.51rem', marginTop: '1.02rem', position: 'relative' }}>
                        <img 
                          src={product.image_url} 
                          alt={product.title}
                          style={{
                            width: '100%',
                            height: '81.6px',
                            objectFit: 'contain',
                            backgroundColor: '#f8f9fa',
                            borderRadius: '4.08px'
                          }}
                          onError={(e) => {
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMiA5QzEwLjM0IDkgOSAxMC4zNCA5IDEyczEuMzQgMyAzIDMgMy0xLjM0IDMtM1MxMy42NiA5IDEyIDlaTTEyIDExQzExLjQ1IDExIDExIDExLjQ1IDExIDEyUzExLjQ1IDEzIDEyIDEzIDEzIDEyLjU1IDEzIDEyUzEyLjU1IDExIDEyIDExWiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4K';
                            e.target.style.backgroundColor = '#f3f4f6';
                          }}
                        />
                        
                        {/* Enhanced image search button */}
                        {product.asin && (
                          <button
                            onClick={() => searchBetterImage(product.id, product.asin, product.title)}
                            disabled={imageSearching.has(product.id)}
                            style={{
                              position: 'absolute',
                              top: '0.34rem',
                              left: '0.34rem',
                              backgroundColor: 'rgba(0, 0, 0, 0.7)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '2.72px',
                              padding: '0.17rem 0.34rem',
                              fontSize: '0.476rem',
                              cursor: imageSearching.has(product.id) ? 'not-allowed' : 'pointer',
                              opacity: imageSearching.has(product.id) ? 0.6 : 1
                            }}
                          >
                            {imageSearching.has(product.id) ? '⟳' : '📸'} {imageSearching.has(product.id) ? 'Searching...' : 'Enhance'}
                          </button>
                        )}
                      </div>
                      
                      {/* Product details */}
                      <div style={{ marginBottom: '0.51rem' }}>
                        <h4 style={{ 
                          margin: '0 0 0.34rem 0', 
                          fontSize: '0.612rem', 
                          fontWeight: '600',
                          lineHeight: '1.3',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {product.title}
                        </h4>
                        
                        <div style={{ fontSize: '0.544rem', color: '#666', marginBottom: '0.17rem' }}>
                          <strong>${product.price}</strong> • {product.vendor_name}
                        </div>
                        
                        <div style={{ fontSize: '0.51rem', color: '#888' }}>
                          {product.category} • Added {new Date(product.createdAt?.seconds * 1000).toLocaleDateString()}
                        </div>
                        
                        {product.asin && (
                          <div style={{ fontSize: '0.476rem', color: '#999', marginTop: '0.17rem' }}>
                            ASIN: {product.asin}
                          </div>
                        )}
                      </div>
                      
                      {/* Action buttons */}
                      <div style={{ display: 'flex', gap: '0.34rem' }}>
                        <button
                          onClick={() => window.open(product.vendor_url, '_blank')}
                          style={{
                            flex: 1,
                            padding: '0.34rem',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '2.72px',
                            cursor: 'pointer',
                            fontSize: '0.544rem'
                          }}
                        >
                          🔗 View
                        </button>
                        
                        <button
                          onClick={() => handleDeleteSingle(product.id)}
                          disabled={isLoading}
                          style={{
                            padding: '0.34rem',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '2.72px',
                            cursor: 'pointer',
                            fontSize: '0.544rem'
                          }}
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {filteredProducts.length === 0 && !loadingProducts && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '2.04rem', 
                  color: '#666',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '5.44px'
                }}>
                  <h3 style={{ margin: '0 0 0.34rem 0', fontSize: '1.02rem' }}>No products found</h3>
                  <p style={{ margin: 0 }}>
                    {categoryFilter || statusFilter ? 'Try adjusting your filters' : 'No products in database'}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Status Message */}
      {message && (
        <div style={{
          marginTop: '0.68rem',
          padding: '0.51rem',
          backgroundColor: message.includes('❌') ? '#fee' : '#efe',
          color: message.includes('❌') ? '#c33' : '#363',
          borderRadius: '4.08px',
          fontSize: '0.612rem'
        }}>
          {message}
        </div>
      )}
    </div>
  );
};

export default ProductAdmin;